import { ICommand, IQuery } from 'core-ts-types';
import { AccountIdentityDatastore, AccountModel } from 'data-ng-authentication';
import { CustomContentsServant } from 'data-ng-custom-text';
import * as _4 from 'lodash4';
import { CommunicatorService } from 'ui-ng-component-communicator';
import { TypeUtility } from 'utility-ts-type';
import { APCustomTextQuery } from '../../../custom_texts/queries/custom_text_query';
import { APTerm } from '../../../global/builders/ap_term_builder';
import { ILoadMarkAsMetListCommand } from '../../../global/components/mark_as_met/commands/load_mark_as_met_list_command';
import { APUserModeConfig } from '../../../global/components/user_mode_config/config/ap_user_mode_config';
import { APModel, IAPProgram } from '../../../global/models/ap_model';
import { IInitiateCachedSclSearchConfigCommand } from '../../../smart_catalog/commands/cached_scl_search_config_command';
import { APTermsQuery } from '../../queries/ap_terms_query';
import { LargeCourseGroupSearchResultsQuery } from '../large_course_group/queries/large_course_group_search_results_query';
import { INotificationDelegate } from './../../../global/helpers/notification_delegate';
import { IAPPlannerResourceTab } from './../planner_resources/builders/planner_resource_tab_builder';
import { SetSelectedPlannerResourceTabCommand } from './../planner_resources/commands/set_selected_planner_resource_tab_command';
import { PlannerScheduleAppointmentTab } from './../planner_resources/constants/planner_resource_tabs';
import { APPlannerResourceTabsQuery } from './../planner_resources/queries/planner_resource_tabs_query';

export class APPlanner {
  private isEditable: boolean | string;
  private isExternalregistrationEnabled: boolean = false;
  private shouldShowGoalSets = false;
  private queries: { terms: APTermsQuery };
  private flags: {
    showEditGoalPreference: boolean;
    plannerTabExpanded: boolean;
    smartSearchToggle: boolean;
  };
  private models: {
    program: IAPProgram;
  };
  private programTitle: string;
  private creditsTitle: string;
  private statePrefix: string;
  private smartSearchOnBound = this.smartSearchOn.bind(this);
  private smartSearchOffBound = this.smartSearchOff.bind(this);
  private isAdvisor: boolean;
  private studentName: string;
  private printDate: string;
  private degreeAuditLink = '';

  constructor(
    private $state: ng.ui.IStateService,
    private $scope: ng.IScope,
    private appConfigModel: any,
    private ngAPPlannerResourceTabsQuery: APPlannerResourceTabsQuery,
    private ngAPTermsQuery: APTermsQuery,
    private ngComponentCommunicatorService: CommunicatorService,
    private ngAPUserModeConfig: APUserModeConfig,
    private ngAccountIdentityDatastore: AccountIdentityDatastore,
    private ngAPModel: APModel,
    private ngAPCustomTextQuery: APCustomTextQuery,
    private ngAPNotificationDelegate: INotificationDelegate,
    private accountModel: AccountModel,
    private loadGoalSetsCommand: ICommand,
    private ngAPStudentDetailsQuery: IQuery,
    private loadAcademicPlanCommand: ICommand,
    private loadFavoriteCoursesCommand: ICommand,
    private loadGradeConfigsCommand: ICommand,
    private loadAlternatePlanListCommand: ICommand,
    private loadMarkAsMetListCommand: ILoadMarkAsMetListCommand,
    private initiateCachedSclSearchConfigCommand: IInitiateCachedSclSearchConfigCommand,
    private setSelectedPlannerResourceTabCommand: SetSelectedPlannerResourceTabCommand,
    private ngLargeCourseGroupSearchResultsQuery: LargeCourseGroupSearchResultsQuery,
    private ngCustomContentsServant: CustomContentsServant
  ) {
    this.queries = {
      terms: {} as APTermsQuery
    };
    this.flags = {
      showEditGoalPreference: false,
      plannerTabExpanded: false,
      smartSearchToggle: false
    };
    this.updateModels();
    this.printDate = `Date Printed: ${moment().format('MMMM DD, YYYY ')}`;
  }

  smartSearchOn() {
    this.flags.smartSearchToggle = true;
  }
  smartSearchOff() {
    this.flags.smartSearchToggle = false;
  }

  gotoChangelog() {
    const changelogRoute = this.$state.includes('ap-advisor') ? 'ap-advisor.changelog' : 'my.planner.changelog';
    this.$state.go(changelogRoute);
  }

  $onInit() {
    this.ngAPTermsQuery.result.terms = [];
    this.ngAPTermsQuery.result.plannedTerms = [];
    const academicPlanApiUrl = _4.get(this.$state, `current.data.academicPlanUrl`, '');
    this.shouldShowGoalSets = this.appConfigModel.isFeatureEnabled('cat-show_student_goal_sets');
    if (this.shouldShowGoalSets) {
      // if user is advisor then make sure to get the student id from studentDetails not from accountModel
      const studentId = this.ngAPUserModeConfig.isAdvisor()
        ? _4.get(this.ngAPStudentDetailsQuery, 'result.id', 0)
        : _4.get(this.accountModel, 'student_nk', 0);
      this.loadGoalSetsCommand(studentId);
    }

    this.loadAcademicPlanCommand(academicPlanApiUrl).then(() => {
      this.initialize();
      this.doOnChanges();
      // Dont make API call to get alternateplans if no program assigned to student SGD-14527
      if (this.ngAPModel.currentProgram().id > 0) {
        this.loadAlternatePlanListCommand();
      }
      this.loadFavoriteCoursesCommand();
      this.loadMarkAsMetListCommand();
    });

    this.ngComponentCommunicatorService.subscribe('SMART_SEARCH_TOGGLE_ON', this.smartSearchOnBound);
    this.ngComponentCommunicatorService.subscribe('SMART_SEARCH_TOGGLE_OFF', this.smartSearchOffBound);
    this.initiateCachedSclSearchConfigCommand();
    this.getScheduleAppointmentLabel();
  }

  switchPlannerResourcesRegion(event: CustomEvent) {
    this.ngComponentCommunicatorService.publish('CLOSE_LARGE_COURSE_GROUP_PANEL');
    const selectedPlannerResourceRegionKey = _4.toUpper(
      _4.get(event, 'detail.value.key', 'MAP')
    ) as IAPPlannerResourceTab['key'];

    if (selectedPlannerResourceRegionKey === 'SCHEDULE_APPOINTMENT') {
      const url = `${window.location.origin}/app/${this.$state.href('my.appointments')}`;
      return window.open(url);
    }

    const isSmartCatalogEnabled = this.appConfigModel.isFeatureEnabled('academicplan-smart_catalog');
    if (selectedPlannerResourceRegionKey === 'SEARCH' && isSmartCatalogEnabled) {
      const notificationName = 'search-loading-notification';
      this.ngAPNotificationDelegate.info({
        content: `<div l-no-wrap l-kind="hort" l-align="center" l-width-fixed><hi-indicator theme="white" size='small' class="mrm"></hi-indicator>Loading the Search Screen</div>`,
        name: notificationName
      });
      return this.initiateCachedSclSearchConfigCommand().finally(() =>
        this.setSelectedPlannerResourceTabCommand(selectedPlannerResourceRegionKey).then(() =>
          this.ngAPNotificationDelegate.dismissAlertByName(notificationName)
        )
      );
    }

    return this.setSelectedPlannerResourceTabCommand(selectedPlannerResourceRegionKey);
  }

  updateModels() {
    this.models = {
      program: _4.head(_4.toArray(this.ngAPModel.read().program))
    };
  }
  initialize() {
    this.updateModels();
    this.isAdvisor = this.ngAPUserModeConfig.isAdvisor();
    this.studentName = !this.isAdvisor
      ? `${this.ngAccountIdentityDatastore.store.fullname}`
      : `${this.ngAPStudentDetailsQuery.result.first_name} ${this.ngAPStudentDetailsQuery.result.last_name}`;
  }

  getScheduleAppointmentLabel() {
    if (this.accountModel.student_nk.startsWith('FAKE')) {
      return;
    }
    this.ngCustomContentsServant.getButtonLabel(PlannerScheduleAppointmentTab.title).then((label: string) => {
      this.$scope.$apply();
    });
  }

  $onDestroy() {
    this.ngComponentCommunicatorService.release('SMART_SEARCH_TOGGLE_ON', this.smartSearchOnBound);
    this.ngComponentCommunicatorService.release('SMART_SEARCH_TOGGLE_OFF', this.smartSearchOffBound);
  }
  $onChanges() {
    this.degreeAuditLink = _4.get(this.ngAPCustomTextQuery.result['AP_DEGREE_AUDIT_URL'], 'text', '');
    this.doOnChanges();
  }

  get programTitleText() {
    return this.models.program
      ? `${this.models.program.name} ${this.models.program.short_credential}`
      : 'Program Title';
  }

  doOnChanges() {
    this.queries = {
      terms: this.ngAPTermsQuery
    };

    this.isAdvisor = this.ngAPUserModeConfig.isAdvisor();
    this.isEditable = _4.isBoolean(this.isEditable) ? this.isEditable : this.isEditable === 'true';

    this.statePrefix = this.isAdvisor ? 'ap-advisor' : 'my.planner';
    this.flags.showEditGoalPreference =
      this.isEditable && this.appConfigModel.isFeatureEnabled('academicplan-allow_autoloading_plan');
    this.programTitle = this.programTitleText;
    this.creditsTitle = this.models.program
      ? `${TypeUtility.NumberUtility.stripTrailingZeroes(this.models.program.credits)} Credits`
      : 'X Credits';
    this.isExternalregistrationEnabled = !this.appConfigModel.isFeatureEnabled('reg-scheduler');
  }

  gotoEditPreference() {
    this.$state.go(this.statePrefix + '.preview_plan.edit_preference', {}, { reload: true });
  }
  print() {
    // @ts-ignore
    this.printDate = `Date Printed: ${moment().format('MMMM DD, YYYY ')}`;
    window.print();
  }
}

class APPlannerComponent implements ng.IComponentOptions {
  public controller: any;
  public templateUrl: any;
  public bindings: any;

  constructor() {
    this.controller = APPlanner;
    (this.bindings = {
      isEditable: '@?'
    }),
      (this.templateUrl = 'capability-ng-academic-planner/src/plan/components/planner/planner.html');
  }
}

angular.module('capability-ng-academic-planner').component('apPlanner', new APPlannerComponent());



// WEBPACK FOOTER //
// ./app/vendor/capability-ng-academic-planner/src/plan/components/planner/planner.ts